import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IContainerItem, IContainerItemListResponse, mapToContainerItem } from '../common/ContainerSchemas';
import { ItemsApiProvider } from '../providers/ItemsApiProvider';
import { Constants } from '../common/Constants';

const itemsApi = ItemsApiProvider.instance;

export interface UseContainerDataResult {
  containerItems: IContainerItem[];
  folderPath: IContainerItem[];
  parentId: string;
  totalResults: number;
  isUILoading: boolean;
  isFolderLoading: boolean;
  isSearchLoading: boolean;
  error: Error | null;
  searchQuery: string;
  searchResults: IContainerItem[];
  page: number;
  pageSize: number;
  isAllContainer: boolean;
  effectiveContainerId: string;
  setFolderPath: (p: IContainerItem[]) => void;
  setParentId: (id: string) => void;
  setSearchQuery: (q: string) => void;
  setSearchResults: (items: IContainerItem[]) => void;
  setIsSearchLoading: (b: boolean) => void;
  setPage: (n: number) => void;
  setPageSize: (n: number) => void;
  setTotalResults: (n: number) => void;
  refresh: () => void;
  refreshFolderItems: () => Promise<void>;
  onFolderClicked: (folder: IContainerItem) => void;
  onBreadcrumbClick: (folder: IContainerItem) => void;
  resetError: () => void;
}

export function useContainerData(containerId?: string): UseContainerDataResult {
  const [parentId, setParentId] = useState<string>('root');
  const [containerItems, setContainerItems] = useState<IContainerItem[]>([]);
  const [folderPath, setFolderPath] = useState<IContainerItem[]>([]);
  const [isFolderLoading, setIsFolderLoading] = useState<boolean>(true);
  const [isUILoading, setIsUILoading] = useState<boolean>(true);
  const [searchResults, setSearchResults] = useState<IContainerItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    parseInt(Constants.VITE_APP_CONTAINER_PAGE_SIZE),
  );
  const [totalResults, setTotalResults] = useState<number>(0);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectContainerId, setSelectContainerId] = useState<string | undefined>(
    undefined,
  );
  const folderLoadingRef = useRef<boolean>(false);

  const isAllContainer = useMemo(
    () => containerId?.toLowerCase() === 'all',
    [containerId],
  );

  const effectiveContainerId = useMemo(() => {
    if (!isAllContainer) {
      return containerId ?? '';
    }
    const fromFolderPath = folderPath.find((f) => f.container_id)?.container_id;
    return fromFolderPath ?? selectContainerId ?? containerId ?? '';
  }, [containerId, isAllContainer, folderPath, selectContainerId]);

  useEffect(() => {
    const timer = setTimeout(() => setIsUILoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const refreshFolderItems = useCallback(async () => {
    if (!containerId || folderLoadingRef.current) {
      return;
    }
    folderLoadingRef.current = true;
    setIsFolderLoading(true);

    try {
      if (parentId === 'root') {
        if (isAllContainer) {
          const itemsSearch = await itemsApi.search('', page, pageSize);
          const items = itemsSearch.items.map(mapToContainerItem);

          setSelectContainerId(undefined);
          setContainerItems(items);
          setTotalResults(itemsSearch.totalCount || 0);
          return;
        } else {
          const response = await itemsApi.list(containerId, page, pageSize);
          setTotalResults(response.total_count);
          setContainerItems(response.items);
          return;
        }
      }

      const folderContainerId = isAllContainer ? effectiveContainerId : containerId;
      const response = await itemsApi.get(folderContainerId, parentId, page, pageSize);
      const totalCount = (response as IContainerItemListResponse).total_count ?? 1;
      const items = response.hasOwnProperty('items')
        ? (response as IContainerItemListResponse).items
        : [response as IContainerItem];
      setContainerItems(items);
      setTotalResults(totalCount);
    } catch {
      setContainerItems([]);
    } finally {
      setIsFolderLoading(false);
      folderLoadingRef.current = false;
    }
  }, [containerId, parentId, page, pageSize, isAllContainer, effectiveContainerId]);

  const refresh = useCallback(() => {
    if (!folderLoadingRef.current) {
      refreshFolderItems();
    }
  }, [refreshFolderItems]);

  useEffect(() => {
    if (!containerId) return;
    setContainerItems([]);
    setPage(1);
    setTotalResults(0);
    setSearchQuery('');
    setSearchResults([]);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, parentId]);

  useEffect(() => {
    if (!containerId || searchQuery.trim().length > 0) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchQuery]);

  const setLocation = (newPath: IContainerItem[]) => {
    const newParentId =
      newPath.length > 0 ? newPath[newPath.length - 1].id : 'root';
    if (newParentId !== parentId) {
      setFolderPath(newPath);
      setParentId(newParentId);
    }
  };

  const onBreadcrumbClick = (folder: IContainerItem) => {
    const idx = folderPath.findIndex((f) => f.id === folder.id);
    const newPath = idx >= 0 ? folderPath.slice(0, idx + 1) : [];
    setLocation(newPath);
  };

  const onFolderClicked = (folder: IContainerItem) => {
    setSearchQuery('');
    setSearchResults([]);
    if (isAllContainer && folder.container_id) {
      setSelectContainerId(folder.container_id);
    }
    setLocation([...folderPath, folder]);
  };

  const resetError = () => setError(null);

  return {
    containerItems,
    folderPath,
    parentId,
    totalResults,
    isUILoading,
    isFolderLoading,
    isSearchLoading,
    error,
    searchQuery,
    searchResults,
    page,
    pageSize,
    isAllContainer,
    effectiveContainerId: effectiveContainerId,
    refreshFolderItems,
    setFolderPath,
    setParentId,
    setSearchQuery,
    setSearchResults,
    setIsSearchLoading,
    setPage,
    setPageSize,
    setTotalResults,
    refresh,
    onFolderClicked,
    onBreadcrumbClick,
    resetError,
  };
}
